import React, { useState, useRef, useEffect } from "react";
import Select, { components } from "react-select";

/**
 * Custom Option component with checkbox for multi-select
 */
const CheckboxOption = (props) => (
  <div>
    <components.Option {...props}>
      {props.selectProps.isMulti && (
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null}
          style={{
            marginRight: "8px",
            color: props.isSelected ? "white" : "inherit",
          }}
        />
      )}
      <label>{props.label}</label>
    </components.Option>
  </div>
);

const CustomValueContainer = (props) => {
  const { children, getValue, selectProps } = props;

  if (!selectProps.isFilter) {
    return (
      <components.ValueContainer {...props}>
        {children}
      </components.ValueContainer>
    );
  }

  const count = getValue().length;
  const input = children[children.length - 1];

  return (
    <components.ValueContainer {...props}>
      {!selectProps.menuIsOpen && (
        <span
          style={{
            marginLeft: 8,
            whiteSpace: "nowrap",
          }}
        >
          {count === 0
            ? selectProps.placeholder
            : count === 1
              ? getValue()[0].label
              : `${count} selected`}
        </span>
      )}
      <div
        style={{
          position: selectProps.menuIsOpen ? "static" : "absolute",
          opacity: selectProps.menuIsOpen ? 1 : 0,
          pointerEvents: selectProps.menuIsOpen ? "auto" : "none",
        }}
      >
        {input}
      </div>
      {/* {selectProps.menuIsOpen && input} */}
    </components.ValueContainer>
  );
};
/**
 * MultiSelect component with checkbox options and "Select All" functionality
 *
 * @param {string} id - Unique identifier for the form field
 * @param {Array|string} value - Current selected value(s)
 * @param {Array} optionsList - Array of {value, label} option objects
 * @param {string} placeholder - Placeholder text
 * @param {Function} setFormData - Function to update parent form state
 * @param {Function} onChange - Function to handle value changes
 * @param {Object} formData - Current form data
 * @param {boolean} disabled - Whether the select is disabled
 * @param {boolean} isMulti - Enable multi-select mode (default: true)
 * @param {boolean} showSelectAll - Show "Select All" option (default: true)
 */
const MultiSelectWithCheckboxes = ({
  id,
  value,
  optionsList,
  placeholder,
  setFormData,
  formData,
  disabled,
  isMulti = true,
  showSelectAll = true,
  required = false,
  onChange,
  isFilter = false,
}) => {
  // ==================== STATE & REFS ====================
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectRef = useRef(null);
  const previousValueRef = useRef(value);

  const updateValue = (values) => {
    if (onChange) {
      onChange(values);
    } else {
      setFormData({ ...formData, [id]: values });
    }
  };

  // ==================== OPTION PROCESSING ====================

  const allOption =
    isMulti && showSelectAll && optionsList?.length > 0
      ? { value: "SELECT_ALL", label: "Select All" }
      : null;

  // Get all original options from props
  const nonAllOptions = optionsList || [];

  // Combine them so React-Select renders the Select All option at the top
  const finalOptionsList = allOption
    ? [allOption, ...nonAllOptions]
    : nonAllOptions;
  // /**
  //  * Find the "Select All" option (only for multi-select mode)
  //  * Looks for options with empty value or "Select/Deselect all" label
  //  */
  // const allOption = isMulti && showSelectAll
  //   ? optionsList?.find(
  //       (option) =>
  //         option.value === "" || option.label === "Select/Deselect all"
  //     )
  //   : null;
  // /**
  //  * Get all options except the "Select All" option
  //  * Used for determining when all items are selected
  //  */
  // const nonAllOptions = isMulti
  //   ? optionsList?.filter(
  //       (option) =>
  //         option.value !== "" && option.label !== "Select/Deselect all"
  //     )
  //   : optionsList;

  // ==================== EFFECTS ====================
  /**
   * Update previous value reference for comparison
   * Only runs in multi-select mode
   */
  useEffect(() => {
    if (!isMulti) return;
    previousValueRef.current = value || [];
  }, [value, isMulti]);

  // ==================== STYLING ====================

  const filterStyles = isFilter
    ? {
        minHeight: "2.25rem",
        height: "2.25rem",
        overflow: "hidden",
      }
    : {
        minHeight: "2.25rem",
        // height: "2.25rem",
      };
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      ...filterStyles,
      backgroundColor: !isFilter ? "transparent" : "#18a587",
      border: state.isFocused ? "1px solid #18a587" : "1px solid #18a587",
      boxShadow: state.isFocused ? 0 : 0,
      "&:hover": { border: "1px solid #18a587" },
      color: !isFilter ? "inherit" : "white",
      fontSize: "inherit",
    }),
    // 1. DROPDOWN ARROW COLOR
    dropdownIndicator: (provided, state) => ({
      ...provided,
      padding: "0 0.5rem", // added
      color: !isFilter ? "#666" : "#ffffff", // Arrow color
      "&:hover": {
        color: !isFilter ? "#18a587" : "#e0e0e0", // Hover color
      },
    }),

    // 2. CLEAR ALL "X" COLOR (On the far right side of control)
    clearIndicator: (provided, state) => ({
      ...provided,
      padding: "0 0.5rem", // added
      color: !isFilter ? "#666" : "#ffffff", // Clear icon color
      "&:hover": {
        color: "#f0142f", // Hover color
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      ...filterStyles,
      padding: "0 0.5rem", // added
    }),

    // 3. VERTICAL SEPARATOR LINE (Optional: adjust or hide)
    indicatorSeparator: (provided) => ({
      ...provided,
      marginTop: "0.5rem", // added
      marginBottom: "0.5rem", // added
      backgroundColor: !isFilter ? "#f0ebeb" : "rgba(255, 255, 255, 0.4)",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "inherit",
    }),
    menu: (provided) => ({
      ...provided,
      width: "max-content", // Expands the menu to fit the longest text label
      minWidth: "100%", // Ensures it never shrinks smaller than the control button
      maxWidth: "400px", // Optional safety cap so it doesn't overflow the screen
    }),
    option: (provided, state) => ({
      ...provided,
      whiteSpace: "normal", // Allows long text to wrap if it exceeds max-width
      wordBreak: "break-word",
      backgroundColor: state.isSelected
        ? "#18a587"
        : state.isFocused
          ? "#63c3ae"
          : provided.backgroundColor,
      color: state.isFocused || state.isSelected ? "white" : "inherit",
      "& input[type='checkbox']": {
        accentColor: isFilter ? "#adeadd" : "inherit",
      },
    }),
    multiValue: (provided, state) => ({
      ...provided,
      backgroundColor: "#18a587",
      display:
        allOption && state.data.value === allOption.value ? "none" : "flex",
    }),
    multiValueLabel: (provided) => ({ ...provided, color: "#fff" }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#fff",
      ":hover": { backgroundColor: "#f0142f" },
    }),
    singleValue: (provided) => ({ ...provided, color: "inherit" }),
  };

  // ==================== HANDLERS ====================
  /**
   * Handle selection changes
   * Uses action-based detection for reliable "Select All" functionality
   */
  const handleSelectionChange = (selectedOptions, action) => {
    console.log("Selection changed:", { selectedOptions, action });

    // Handle single-select mode
    if (!isMulti) {
      const selectedValue = selectedOptions ? selectedOptions.value : "";
      // setFormData({ ...formData, [id]: selectedValue });
      updateValue(selectedValue);
      setMenuIsOpen(false);
      return;
    }

    // Handle multi-select mode
    const currentSelectedValues =
      selectedOptions?.map((opt) => opt.value) || [];

    if (action.action === "select-option") {
      handleOptionSelected(action.option, currentSelectedValues);
    } else if (action.action === "deselect-option") {
      handleOptionDeselected(action.option, currentSelectedValues);
    } else {
      // Handle other actions (clear, etc.)
      // setFormData({ ...formData, [id]: currentSelectedValues });
      const cleanValues = currentSelectedValues.filter(
        (val) => val !== "SELECT_ALL",
      );
      updateValue(cleanValues);
    }

    // Keep menu open for multi-select
    setMenuIsOpen(true);
  };

  /**
   * Handle when an option is selected
   */
  const handleOptionSelected = (clickedOption, currentSelectedValues) => {
    if (allOption && clickedOption.value === allOption.value) {
      // "Select All" clicked -> Save ONLY the real option values to state
      console.log("Select All clicked");
      const allRealValues = nonAllOptions.map((opt) => opt.value);
      updateValue(allRealValues);
    } else {
      // Regular option was selected
      console.log("Regular option selected:", clickedOption.label);

      // Check if all non-All options are now selected
      const allNonAllSelected = nonAllOptions.every((option) =>
        currentSelectedValues.includes(option.value),
      );

      if (allNonAllSelected && allOption) {
        // Auto-add "Select All" when all individual options are selected
        // But store only the actual values (excluding empty string)
        const actualValues = currentSelectedValues.filter(
          (val) => val !== "SELECT_ALL" && val !== "",
        );
        // setFormData({ ...formData, [id]: actualValues });
        updateValue(actualValues);
      } else {
        // Filter out the "Select All" option from stored values
        const actualValues = currentSelectedValues.filter((val) => val !== "");
        // setFormData({ ...formData, [id]: actualValues });
        updateValue(actualValues);
      }
    }
  };

  /**
   * Handle when an option is deselected
   */
  const handleOptionDeselected = (clickedOption, currentSelectedValues) => {
    if (allOption && clickedOption.value === allOption.value) {
      // "Select All" was deselected - clear all selections
      console.log("Select All deselected");
      // setFormData({ ...formData, [id]: [] });
      updateValue([]);
    } else {
      // Regular option was deselected
      console.log("Regular option deselected:", clickedOption.label);

      // Remove "Select All" option if it was selected
      const newValues = currentSelectedValues.filter(
        (val) => val !== "SELECT_ALL",
      );
      // setFormData({ ...formData, [id]: newValues });
      updateValue(newValues);
    }
  };

  // ==================== VALUE PREPARATION ====================
  /**
   * Prepare value for the Select component
   * Converts value IDs to option objects for display
   */
  const selectValue = isMulti
    ? value
        ?.map((val) => optionsList?.find((option) => option.value === val))
        ?.filter(Boolean) // Remove any undefined values
    : optionsList?.find((option) => option.value === value);

  // ==================== RENDER ====================
  return (
    <Select
      required={required}
      ref={selectRef}
      id={id}
      value={selectValue}
      options={finalOptionsList}
      isFilter={isFilter}
      onChange={handleSelectionChange}
      isMulti={isMulti}
      styles={{
        ...customStyles,
        // Crucial: Elevate the z-index of the portaled menu to sit above modals/cards
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      }}
      components={{
        Option: CheckboxOption,
        ValueContainer: CustomValueContainer, // <-- add this
      }}
      className="basic-multi-select"
      classNamePrefix="select"
      placeholder={placeholder}
      menuIsOpen={menuIsOpen}
      isDisabled={disabled}
      hideSelectedOptions={false}
      onMenuOpen={() => {
        setMenuIsOpen(true);

        if (isFilter) {
          setTimeout(() => {
            if (selectRef.current) {
              selectRef.current.focusInput();
            }
          }, 0);
        }
      }}
      onMenuClose={() => setMenuIsOpen(false)}
      menuPortalTarget={typeof window !== "undefined" ? document.body : null}
      menuPosition="fixed"
    />
  );
};

export default MultiSelectWithCheckboxes;
